import React, { useState, useMemo } from 'react';
import {
  Tag,
  Plus,
  Pencil,
  Trash2,
  X,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import {
  useGetAllCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} from '../../api/apiSlice';
import { toast } from 'react-hot-toast';
import ConfirmDialog from '../../components/common/ConfirmDialog';

// CategoriesPage
// ──────────────
// Старая версия игнорила dark mode полностью — белый шрифт на белом
// списке, заголовки таблицы серые. Эта версия — единый dark-aware стиль,
// согласованный с AdminDashboard / pages/admin/*.
//
// Дизайн:
//  • Список слева, форма справа (sticky на desktop)
//  • Inline action-кнопки (edit/delete) с состояниями
//  • ConfirmDialog вместо window.confirm — единый UX с остальной админкой
//  • Иерархия: показываем родителя как «Родитель → Дочерняя»
const CategoriesPage = () => {
  const { data: categories = [], isLoading } = useGetAllCategoriesQuery();
  const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();
  const [deleteCategory, { isLoading: isDeleting }] = useDeleteCategoryMutation();

  const [title, setTitle] = useState('');
  const [parentId, setParentId] = useState('');
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Название категории обязательно');
      return;
    }

    const payload = { title: title.trim() };
    if (parentId) payload.parentId = parentId;

    try {
      if (editing) {
        await updateCategory({ id: editing._id, ...payload }).unwrap();
        toast.success('Категория обновлена');
        setEditing(null);
      } else {
        await createCategory(payload).unwrap();
        toast.success('Категория создана');
      }
      setTitle('');
      setParentId('');
    } catch (err) {
      toast.error(err?.data?.message || 'Ошибка при сохранении');
    }
  };

  const handleEdit = (cat) => {
    setEditing(cat);
    setTitle(cat.title);
    setParentId(cat.parentId?._id || cat.parentId || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditing(null);
    setTitle('');
    setParentId('');
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteCategory(pendingDelete._id).unwrap();
      toast.success('Категория удалена');
    } catch (err) {
      toast.error(err?.data?.message || 'Ошибка при удалении');
    } finally {
      setPendingDelete(null);
    }
  };

  // Родительская выпадайка: исключаем саму себя, чтобы не циклить.
  const parentOptions = useMemo(
    () => categories.filter((c) => !editing || c._id !== editing._id),
    [categories, editing]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Управление категориями
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Категории используются в каталоге курсов для фильтрации. Поддерживается
          одноуровневая вложенность (родитель → дочерняя).
        </p>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 sm:p-5"
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            placeholder="Название категории"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <select
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:w-56"
          >
            <option value="">Без родителя</option>
            {parentOptions.map((cat) => (
              <option key={cat._id} value={cat._id}>
                {cat.title}
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={isCreating || isUpdating}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating || isUpdating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : editing ? (
              <Pencil className="w-4 h-4" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {editing ? 'Сохранить' : 'Создать'}
          </button>
          {editing && (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="inline-flex items-center gap-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-2 text-sm font-medium"
            >
              <X className="w-4 h-4" /> Отмена
            </button>
          )}
        </div>
      </form>

      {/* List */}
      {categories.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-10 text-center text-gray-500 dark:text-gray-400">
          <Tag className="w-10 h-10 mx-auto mb-2 text-gray-400" />
          <p>Категорий ещё нет</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Название</th>
                  <th className="px-4 py-3 text-left font-semibold">Родитель</th>
                  <th className="px-4 py-3 text-right font-semibold">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {categories.map((cat) => (
                  <tr
                    key={cat._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition"
                  >
                    <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">
                      {cat.parentId?.title && (
                        <span className="text-xs text-gray-400 inline-flex items-center mr-1">
                          {cat.parentId.title}
                          <ChevronRight className="w-3 h-3 mx-0.5" />
                        </span>
                      )}
                      {cat.title}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {cat.parentId?.title || (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => handleEdit(cat)}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 px-2.5 py-1.5 text-xs font-medium"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Изменить</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDelete(cat)}
                          className="inline-flex items-center gap-1 rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50 text-red-700 dark:text-red-300 px-2.5 py-1.5 text-xs font-medium"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Удалить</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Удалить категорию?"
        description={`Категория «${pendingDelete?.title || ''}» будет удалена. Курсы, использующие эту категорию, потеряют привязку.`}
        confirmLabel="Удалить"
        destructive
        loading={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default CategoriesPage;
